'use client';

import { useId, useMemo, useState } from 'react';
import { Label } from '@savvy-kit/components/label';
import { Text } from '@savvy-kit/foundations/text';
import SearchableCombobox from '@savvy-kit/patterns/searchable-combobox';
import { isNil } from 'es-toolkit';
import { graphql } from 'src/gql';
import { snakeCaseToHumanCase } from 'src/utils/formatting';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { AdvisorRole } from './advisor-role-select';

const ALL_ADVISORS_QUERY = graphql(/* GraphQL */ `
  query AllAdvisors {
    allAdvisors {
      id
      salesforceUserId
      user {
        id
        firstName
        lastName
        email
        role
      }
      advisorGroups {
        id
        name
        representationType
      }
    }
  }
`);

export type Advisor = {
  id: string;
  salesforceUserId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: 'CSA' | 'ADMIN' | 'ADMIN_CSA' | 'ADVISOR' | 'ASSISTANT' | 'CLIENT' | 'NON_ADVISORY_USER' | 'READ_ONLY_ADMIN';
  };
  advisorGroups:
    | {
        id: string;
        name: string;
        representationType: 'individual' | 'group';
      }[]
    | null;
};

type AdvisorSelectProps = {
  readonly onAdvisorSelected: (advisor: Advisor | null) => void;
  readonly selectedAdvisor: Advisor | null;
  readonly roleFilter?: AdvisorRole;
  readonly required?: boolean;
  /** Surfaces the required error without waiting for a blur, so a parent's submit gate can explain itself. */
  readonly showRequiredError?: boolean;
};

export function AdvisorSelect({
  onAdvisorSelected,
  selectedAdvisor,
  roleFilter = 'ADVISOR',
  required = false,
  showRequiredError = false,
}: AdvisorSelectProps) {
  const { data, loading } = useQueryWithErrorLogging(ALL_ADVISORS_QUERY);
  const [searchQuery, setSearchQuery] = useState('');
  const [touched, setTouched] = useState(false);
  const inputId = useId();

  const advisorsByRole = useMemo(() => {
    if (!Array.isArray(data?.allAdvisors)) return [];
    if (roleFilter === 'ALL_ROLES') return data.allAdvisors;
    return data.allAdvisors.filter((advisor) => advisor.user.role === roleFilter);
  }, [data?.allAdvisors, roleFilter]);

  const filteredAdvisors = useMemo(() => {
    if (!searchQuery.trim()) return advisorsByRole;
    const normalizedQuery = searchQuery.toLowerCase().trim();
    return advisorsByRole.filter((advisor) => {
      const fullName = `${advisor.user.firstName} ${advisor.user.lastName}`.toLowerCase();
      const email = advisor.user.email.toLowerCase();
      return fullName.includes(normalizedQuery) || email.includes(normalizedQuery);
    });
  }, [advisorsByRole, searchQuery]);

  const options = useMemo(
    () =>
      filteredAdvisors.map((advisor) => ({
        value: advisor.id,
        label: `${advisor.user.firstName} ${advisor.user.lastName}`,
      })),
    [filteredAdvisors],
  );

  const advisorsById = useMemo(() => new Map(advisorsByRole.map((advisor) => [advisor.id, advisor])), [advisorsByRole]);

  const showError = (touched || showRequiredError) && required && !selectedAdvisor;
  const errorMessage = showError ? 'Advisor is required' : '';

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId}>Select an advisor</Label>
      <SearchableCombobox
        id={inputId}
        options={options}
        value={selectedAdvisor?.id ?? ''}
        onValueChange={(value) => {
          setSearchQuery('');
          onAdvisorSelected(advisorsById.get(value) ?? null);
        }}
        onSearchChange={setSearchQuery}
        onInputBlur={() => {
          setTouched(true);
        }}
        shouldFilter={false}
        clearable
        isLoading={loading}
        placeholder="Start typing name or email to search..."
        emptyMessage="No advisors found"
        error={Boolean(errorMessage)}
        errorMessage={errorMessage}
        // Also renders inside the MUI Create prospect dialog, whose z-index is 1300.
        popoverPositionerClassName="z-[1400]"
        renderOption={(option) => {
          const advisor = advisorsById.get(option.value);
          return (
            <div className="flex flex-col">
              <Text>{option.label}</Text>
              {!isNil(advisor) && (
                <Text variant="body-sm" color="secondary">
                  {snakeCaseToHumanCase(advisor.user.role)} • {advisor.user.email}
                </Text>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
